import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EventEmitter, Injectable, Output } from '@angular/core';

import { firstValueFrom } from 'rxjs';

@Injectable()
export class ApiService {
  @Output()
  unAuthorized = new EventEmitter<void>();

  public headers: { [key: string]: string } = {
    'Content-Type': 'application/json',
  };

  constructor(public http: HttpClient) {}

  /**
   * Set authorization bearer token to request headers
   * @param token - token string
   */
  setToken(token: string): void {
    // eslint-disable-next-line
    this.headers['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Remove authorization bearer token from request headers
   */
  removeToken(): void {
    // eslint-disable-next-line
    delete this.headers['Authorization'];
  }

  /**
   * Return options help method to wrap headers and options
   * @param opts - Optionas to be assigned and passed to the HttpHeader
   * @returns
   */
  private async getOptions(opts?: {}): Promise<{}> {
    const headers = new HttpHeaders(this.headers);
    return Object.assign({}, { headers }, opts || {});
  }

  /**
   * Get API helper function that wraps a http get request as a Promise
   * @param url - url string url
   * @param options - options object
   * @returns a response object as a Promise
   */
  async get(url: string, options = {}): Promise<any> {
    const usedOptions = await this.getOptions(options);
    const getResponse$ = this.http.get(url, usedOptions);
    return firstValueFrom(getResponse$)
      .then((response: any) => {
        return Promise.resolve(response.data);
      })
      .catch((e) => {
        if (e.status === 401) {
          this.unAuthorized.emit();
        }
        throw e;
      });
  }

  /**
   * Post API helper function that wraps a http post request as a Promise
   * @param url - url string url
   * @param body - body object
   * @param options - options object
   * @returns a response object as a Promise
   */
  async post(url: string, body: any, options = {}): Promise<any> {
    const usedOptions = await this.getOptions(options);
    const postResponse$ = this.http.post(url, body, usedOptions);
    return firstValueFrom(postResponse$)
      .then((response: any) => {
        return Promise.resolve(response);
      })
      .catch((e) => {
        if (e.status === 401) {
          this.unAuthorized.emit();
        }
        throw e;
      });
  }

  /**
   * Delete API helper function that wraps a http delete request as a Promise
   * @param url - url string url
   * @param options - body object
   * @returns a response object as a Promise
   */
  async delete(url: string, options = {}): Promise<any> {
    const usedOptions = await this.getOptions(options);
    const deleteResponse$ = this.http.delete(url, usedOptions);
    return firstValueFrom(deleteResponse$)
      .then((response: any) => {
        return Promise.resolve(response);
      })
      .catch((e) => {
        if (e.status === 401) {
          this.unAuthorized.emit();
        }
        throw e;
      });
  }
}
